// Global Ready-signal handler (UC-6). Listens on the presence socket for
// Ready-signals addressed to me and reacts per the spec:
//   • kind "ready" / "challenge" / "accepted" → a non-blocking banner
//     (Join / Accept / Play · Wait · Later); navigation happens only on click,
//     so an incoming signal never yanks me off whatever I'm doing.
//   • kind "wait" / "later"                    → an informational banner.
// The banner lives in a fixed overlay appended to <body>, so it survives SPA
// screen swaps. The persistent turn-alert pill (turnAlerts.ts) is the catch-up
// if the banner is dismissed or missed.

import { api } from "../api.ts";
import { navigate } from "../router.ts";
import {
  acknowledgeNotification,
  onSessionMessage,
  type NotificationEnvelope,
  type ReadySignal,
} from "../presence.ts";
import { esc } from "../dom.ts";
import { focusOrNavigate } from "./tabs.ts";
import { t } from "../i18n/index.ts";

/** Navigate to a Ready-signal's link, focusing an existing board tab if the
 *  signal points at a game already open elsewhere (plan item 5). */
function goToSignal(sig: ReadySignal): void {
  if (sig.game_id) void focusOrNavigate(sig.game_id, () => navigate(sig.link));
  else navigate(sig.link);
}

let container: HTMLElement | undefined;

/** Wire the Ready-signal listener once, at app start. */
export function initReadyBanner(): void {
  onSessionMessage((msg) => {
    if (msg.t === "notification") handleNotification(msg.d);
    else if (msg.t === "ready") handleReady(msg.d);
  });
}

function overlay(): HTMLElement {
  if (!container) {
    container = document.createElement("div");
    container.id = "ready-banners";
    document.body.append(container);
  }
  return container;
}

function handleNotification(notification: NotificationEnvelope): void {
  const kind: ReadySignal["kind"] =
    notification.kind === "match_offered"
      ? "challenge"
      : notification.kind === "rematch_offered"
        ? "rematch"
        : notification.kind === "match_accepted"
          ? "accepted"
          : notification.kind === "match_declined"
            ? "declined"
            : notification.kind === "opponent_waiting"
              ? "waiting"
              : notification.kind === "wait_requested"
                ? "wait"
                : notification.kind === "play_later"
                  ? "later"
                  : "ready";
  const actionUrl = notification.actions.find((action) => action.url)?.url;
  let link = notification.context.link ?? undefined;
  if (!link && actionUrl) {
    try {
      link = new URL(actionUrl, location.origin).pathname;
    } catch {
      /* malformed provider presentation; leave it non-navigable */
    }
  }
  handleReady(
    {
      from: notification.context.from ?? "",
      from_name: notification.context.from_name ?? notification.title,
      kind,
      challenge_id: notification.context.challenge_id ?? "",
      game_id: notification.context.game_id,
      note: notification.context.note,
      link: link ?? "/",
    },
    notification.notification_id,
  );
}

function handleReady(sig: ReadySignal, notificationId?: string): void {
  const who = `<strong>${esc(sig.from_name)}</strong>`;
  if (
    sig.kind === "ready" ||
    sig.kind === "challenge" ||
    sig.kind === "rematch" ||
    sig.kind === "accepted" ||
    sig.kind === "waiting"
  ) {
    // Already where the signal points → nothing to do.
    if (sig.link && location.pathname === sig.link) {
      if (notificationId) acknowledgeWhenVisible(notificationId);
      return;
    }
    // Offer a non-blocking choice; navigate only when the member clicks. We
    // never auto-switch — being idle elsewhere shouldn't rip you off the page.
    const headline =
      sig.kind === "challenge"
        ? t("ready.challengesYou", { who })
        : sig.kind === "rematch"
          ? t("ready.rematch", { who })
          : sig.kind === "accepted"
            ? t("ready.accepted", { who })
            : sig.kind === "waiting"
              ? t("ready.waitingForYou", { who })
              : t("ready.readyToPlay", { who });
    const actions: Action[] = [
      {
        label:
          sig.kind === "challenge" ? t("ready.accept") : sig.kind === "ready" ? t("ready.join") : t("ready.play"),
        kind: "primary",
        act: () => goToSignal(sig),
      },
    ];
    // "Wait / later" replies only make sense for a pending challenge, not for a
    // game that already exists (accepted / waiting both point at a live board)
    // or a rematch offer, which has no challenge to reply to.
    if (sig.kind !== "accepted" && sig.kind !== "waiting" && sig.kind !== "rematch") {
      actions.push(
        {
          label: t("ready.wait"),
          kind: "ghost",
          act: () => void api.sendReady(sig.challenge_id, "wait", t("ready.note")),
        },
        { label: t("ready.later"), kind: "ghost", act: () => void api.sendReady(sig.challenge_id, "later") },
      );
    }
    showBanner(sig, actions, headline, undefined, notificationId);
    return;
  }

  // wait / later / declined → informational, auto-dismissing.
  const verb =
    sig.kind === "wait"
      ? t("ready.askedWait")
      : sig.kind === "declined"
        ? t("ready.declinedChal")
        : t("ready.wantsLater");
  showBanner(sig, [], `${who} ${verb}.`, 9000, notificationId);
}

interface Action {
  label: string;
  kind: "primary" | "ghost";
  act: () => void;
}

/** Render a banner. `headlineHtml` is trusted HTML (callers escape user text). */
function showBanner(
  sig: ReadySignal,
  actions: Action[],
  headlineHtml: string,
  autoDismissMs?: number,
  notificationId?: string,
): void {
  const banner = document.createElement("div");
  banner.className = "ready-banner";

  const note = sig.note ? ` <span class="rb-note">“${esc(sig.note)}”</span>` : "";
  const label = document.createElement("span");
  label.innerHTML = `${headlineHtml}${note}`;
  banner.append(label);

  const spacer = document.createElement("span");
  spacer.className = "spacer";
  banner.append(spacer);

  const arrived = () => { if (sig.link && location.pathname === sig.link) dismiss(); };
  const dismiss = () => { banner.remove(); window.removeEventListener("spa:navigated", arrived); };
  window.addEventListener("spa:navigated", arrived);

  for (const a of actions) {
    const btn = document.createElement("button");
    btn.className = `btn ${a.kind}`;
    btn.type = "button";
    btn.textContent = a.label;
    btn.onclick = () => {
      a.act();
      dismiss();
    };
    banner.append(btn);
  }

  const close = document.createElement("button");
  close.className = "rb-close";
  close.type = "button";
  close.setAttribute("aria-label", t("ready.dismiss"));
  close.textContent = "×";
  close.onclick = dismiss;
  banner.append(close);

  overlay().append(banner);
  if (notificationId) acknowledgeWhenVisible(notificationId, banner);
  if (autoDismissMs) window.setTimeout(dismiss, autoDismissMs);
}

function acknowledgeWhenVisible(notificationId: string, banner?: HTMLElement): void {
  if (document.visibilityState === "visible") {
    acknowledgeNotification(notificationId);
    return;
  }
  const onVisible = () => {
    if (document.visibilityState !== "visible") return;
    document.removeEventListener("visibilitychange", onVisible);
    if (!banner || banner.isConnected) acknowledgeNotification(notificationId);
  };
  document.addEventListener("visibilitychange", onVisible);
}
